import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import { useApi, useBearer } from '@healthblocks-io/core'
import Axios from 'axios'
import { useMemo, useState } from 'react'

import { getBlobFromUri, toJpegBlob } from '@/modules/shared/helpers/file-upload'

import type { MedicationPhotoErrorCode, MedicationPhotoResult } from '@/modules/app/types/HealthParameter.types'

/** The parsed result plus the photo it came from, so the caller can attach it. */
export type MedicationFileOutcome = MedicationPhotoResult & { fileUrl: string }

/** Matches the ceiling the api enforces; the photo is compressed well under it. */
const MAX_PHOTO_BYTES = 10 * 1024 * 1024

/**
 * Capture settings. The api reads the photo at a fixed detail level, so sending
 * more pixels costs upload time and tokens without improving what is read.
 */
const PHOTO_QUALITY = 60
const PHOTO_MAX_WIDTH = 1024

/** Capacitor's wording when the picker is dismissed, which is not a failure. */
const CANCELLED = 'User cancelled photos app'

export type MedicationFileParseError = MedicationPhotoErrorCode | 'capture_failed' | 'photo_too_large'

/**
 * Takes a photo of medication packaging and, unless asked not to, has the api
 * read it.
 *
 * `parse: false` is the parsing feature flag being off. The photo is still
 * captured and handed back, because attaching one is worth doing on its own;
 * only the provider call is skipped, and the result then names no medications.
 *
 * Returns null when the patient backed out of the camera. Failures are
 * surfaced as a code rather than a message so the call site owns the copy.
 */
export function useMedicationFileParse() {
  const [isParsing, setIsParsing] = useState(false)
  const [error, setError] = useState<MedicationFileParseError | null>(null)

  const baseURL = useApi()
  const bearer = useBearer()
  const axios = useMemo(() => Axios.create({ baseURL }), [baseURL])

  const takePhoto = async ({ parse = true }: { parse?: boolean } = {}): Promise<MedicationFileOutcome | null> => {
    setError(null)

    let photo: Blob
    try {
      const captured = await Camera.getPhoto({
        // Native offers the camera or the library. On web `Prompt` opens
        // Capacitor's own camera modal, which goes nowhere on a machine without
        // a usable camera, so the file picker is the only sensible source there.
        source: Capacitor.isNativePlatform() ? CameraSource.Prompt : CameraSource.Photos,
        quality: PHOTO_QUALITY,
        width: PHOTO_MAX_WIDTH,
        resultType: CameraResultType.Uri,
      })
      if (!captured.webPath) {
        throw new Error('No photo returned')
      }

      // Native already hands back JPEG; the web picker returns whatever was selected.
      photo = await toJpegBlob(await getBlobFromUri(captured.webPath))
    } catch (cause) {
      if (cause instanceof Error && cause.message.includes(CANCELLED)) {
        return null
      }

      setError('capture_failed')
      return null
    }

    if (photo.size > MAX_PHOTO_BYTES) {
      setError('photo_too_large')
      return null
    }

    if (!parse) {
      // Same ownership rule as the parsed path: whoever keeps the url revokes it.
      return { medications: [], fallbackReason: null, fileUrl: URL.createObjectURL(photo) }
    }

    setIsParsing(true)
    try {
      const body = new FormData()
      body.append('photo', photo, 'medication.jpg')

      const response = await axios.post<MedicationPhotoResult>('/views/patient/medication/parse-photo', body, {
        headers: { ...(await bearer()), 'Content-Type': 'multipart/form-data' },
      })

      // The object URL outlives this call so the photo can be shown and
      // attached. Whoever keeps it is responsible for revoking it.
      return { ...response.data, fileUrl: URL.createObjectURL(photo) }
    } catch (cause) {
      setError(toErrorCode(cause))

      return null
    } finally {
      setIsParsing(false)
    }
  }

  return { takePhoto, isParsing, error }
}

/**
 * The api names the failure in `code` so the status can be retuned without
 * breaking this. Anything unrecognised is treated as the parser being down,
 * which is the failure the patient can act on by entering medication by hand.
 */
function toErrorCode(cause: unknown): MedicationPhotoErrorCode {
  const code = Axios.isAxiosError(cause)
    ? (cause.response?.data as { code?: MedicationPhotoErrorCode } | undefined)?.code
    : undefined

  return code ?? 'parser_unavailable'
}
